import { Injectable } from '@angular/core';
import { FlexIvrSettings } from '@wephone-core/wephone-core.module';
import { Router } from '@angular/router';
import { HttpEngine } from '@wephone-core/service/http_engine';
import { StorageService } from '@wephone-core/service/storage.service';
import { AuthenticationService, IDomainDict, TDomainDict } from '@wephone-core/service/authentication';
import { CrmType } from '@wephone-core/model/entity/enterprise';
import { BoDomain, BusinessType } from '@wephone-core/system';
import { LoggerService } from '@wephone-utils/services/logger.service';

const defaultDomain = window['_app_domain'];

export interface EnterpriseInfoResp {
  name: string;
  country: string;
  login_options: CrmType[];
}

@Injectable()
export class SigninService {
  static DomainSA = BoDomain;
  private static URL_GET_ENTERPRISE_INFO = '/noauth/enterprise/{domain}';

  protected _domainDict: TDomainDict;
  protected _currentUserName: string;
  protected _currentDomain: string;

  constructor(
    private readonly settings: FlexIvrSettings,
    private readonly router: Router,
    private readonly storage: StorageService,
    private readonly auth: AuthenticationService,
    private readonly loggerService: LoggerService
  ) {
    const domain = this.getDomainForStorage(defaultDomain);
    this._currentDomain = domain;
  }

  get currentUserName(): string {
    return this._currentUserName;
  }

  get currentDomain(): string {
    return this._currentDomain;
  }

  getDomainForUrl(domain: string): string {
    return domain === SigninService.DomainSA ? 'sa' : domain;
  }
  private getDomainForStorage(domain: string): string {
    return domain === 'sa' ? SigninService.DomainSA : domain;
  }

  setCurrentUserName(username: string): void {
    this._currentUserName = username;
  }

  setCurrentDomain(domain: string): void {
    this._currentDomain = domain;
  }

  clearDomainDict(): void {
    this._domainDict = undefined;
  }

  async clearLocalCredential(): Promise<void> {
    this.setCurrentDomain(null);
    this.clearDomainDict();
    await this.auth.clearLastDomain();
  }

  async getDomainDict(): Promise<TDomainDict> {
    if (!this._domainDict) {
      if (!this._currentUserName) {
        throw new Error('No current username');
      }

      let site = 'v3';
      if (this.settings.isProjectSuperAdmin()) {
        site = 'bo';
      } else if (this.settings.isProjectPhone()) {
        site = 'phone';
      }

      const domainDict: TDomainDict = await this.auth.getDomainsBy(this._currentUserName);
      if (domainDict) {
        this._domainDict = {};

        for (const dm of Object.keys(domainDict)) {
          const dmDict: IDomainDict = domainDict[dm];
          if (typeof dmDict !== 'string') {
            const businessType: BusinessType = dmDict.business_type;
            // Not allow super-user access v3 or phone
            if ((site === 'phone' || site === 'v3') && (businessType === BusinessType.RESELLER || !businessType)) {
              continue;
            }

            // Not allow enterprise-user access super
            if (site === 'bo' && businessType && businessType !== BusinessType.RESELLER) {
              continue;
            }
          }

          this._domainDict[dm] = dmDict;
        }
      }

    }
    return this._domainDict;
  }

  setDomainList(domainList: TDomainDict): void {
    this._domainDict = domainList;
  }

  async gotoDomain(domain: string, returnUrl: string = null): Promise<boolean> {
    if (domain === SigninService.DomainSA && !this.settings.isProjectSuperAdmin()) {
      this.loggerService.error('Domain is * but project is not super admin');
      return;
    }

    this._currentDomain = domain;
    await this.auth.setLastDomain(domain);
    await this.storage.setDomainTempData('user_name', this.currentUserName, domain);

    let page = 's/signin';
    if (returnUrl && returnUrl.indexOf('forgot') !== -1) {
      page = 's/forgot';
    }

    if (this.settings.isProjectAdmin() || this.settings.isProjectSuperAdmin()) {
      const prefix: string = this.settings.isProjectAdmin() ? 'v3' : 'bo';
      const domainUrl: string = this.getDomainForUrl(domain);

      const url = window.location.href;
      // this.loggerService.log('url', url);
      let queryString = url ? url.split('?')[1] : window.location.search.slice(1);
      // this.loggerService.log('queryString1', queryString);

      if (url.indexOf('forgot') !== -1) {
        page = 's/forgot';
      }

      if (returnUrl && url.indexOf('returnUrl') === -1) {
        queryString = `${queryString ? queryString : ''}&returnUrl=${encodeURI(returnUrl)}`;
      }
      // this.loggerService.log('queryString2', queryString);

      const newUrl: string = queryString ? `/${prefix}/${domainUrl}/${page}?${queryString}` : `/${prefix}/${domainUrl}`;
      window.location.href = newUrl;
      return true;
    }

    return this.router.navigate([`/${page}`]);
  }

  getEnterpriseInfo(domain: string): Promise<EnterpriseInfoResp> {
    const url = this.settings.getAbsoluteWSv2URL(SigninService.URL_GET_ENTERPRISE_INFO.replace('{domain}', domain));
    return HttpEngine.getInstance().get(url);
  }

}
